import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Building2, Copy, Check, ExternalLink, Link2, Zap, ZapOff, Facebook, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const emptyBrand = {
  name: "", logo_url: "", tone: "", audience_description: "",
  default_sender_email: "", default_sender_phone: "",
  daily_ad_budget: "", industry: "", website: "",
  primary_color: "#7C3AED",
};

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBrand);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showFbImport, setShowFbImport] = useState(false);
  const [fbToken, setFbToken] = useState("");
  const [fbAutoSubscribe, setFbAutoSubscribe] = useState(true);
  const [fbImporting, setFbImporting] = useState(false);
  const [fbResult, setFbResult] = useState(null);
  const [showFbRefresh, setShowFbRefresh] = useState(false);
  const [fbRefreshToken, setFbRefreshToken] = useState("");
  const [fbRefreshing, setFbRefreshing] = useState(false);
  const [fbRefreshResult, setFbRefreshResult] = useState(null);

  const runFbRefresh = async (e) => {
    e.preventDefault();
    if (!fbRefreshToken.trim()) { toast.error("Paste fresh User Access Token"); return; }
    setFbRefreshing(true);
    setFbRefreshResult(null);
    try {
      const { data } = await api.post("/brands/meta/refresh-tokens", {
        user_access_token: fbRefreshToken.trim(),
      });
      setFbRefreshResult(data);
      toast.success(`Refreshed ${data.refreshed} page tokens`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setFbRefreshing(false);
    }
  };

  const runFbImport = async (e) => {
    e.preventDefault();
    if (!fbToken.trim()) {
      toast.error("Paste your User Access Token first");
      return;
    }
    setFbImporting(true);
    setFbResult(null);
    try {
      const { data } = await api.post("/brands/meta/bulk-import", {
        user_access_token: fbToken.trim(),
        auto_subscribe: fbAutoSubscribe,
      });
      setFbResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} brand(s) · ${data.subscribed} subscribed`);
      else toast.info(`No new pages imported (${data.skipped} already linked)`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setFbImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brands");
      setBrands(data.brands || []);
    } catch (e) {
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/auth/me").then(({ data }) => setMe(data.user)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyBrand);
    setShowModal(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "", logo_url: b.logo_url || "", tone: b.tone || "",
      audience_description: b.audience_description || "",
      default_sender_email: b.default_sender_email || "",
      default_sender_phone: b.default_sender_phone || "",
      daily_ad_budget: b.daily_ad_budget ?? "",
      industry: b.industry || "", website: b.website || "",
      primary_color: b.primary_color || "#7C3AED",
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Brand name is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      daily_ad_budget: form.daily_ad_budget === "" ? null : Number(form.daily_ad_budget),
    };
    try {
      if (editing) {
        await api.patch(`/brands/${editing.id}`, payload);
        toast.success("Brand updated");
      } else {
        await api.post("/brands", payload);
        toast.success("Brand created");
      }
      setShowModal(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    if (!confirm(`Delete brand "${b.name}"? Existing leads will be hidden but not deleted.`)) return;
    try {
      await api.delete(`/brands/${b.id}`);
      toast.success("Brand deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copyLandingUrl = async (b) => {
    if (!me?.id) return;
    const origin = window.location.origin;
    const url = `${origin}/lead/${me.id}/${b.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(b.id);
      toast.success("Landing page URL copied!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Brands"
        subtitle="Manage every brand you market for. Pick a brand each time you run a campaign."
        testId="brands-header"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowFbRefresh(true)} className="op-btn-secondary" data-testid="brand-fb-refresh" title="Refresh expired Page Access Tokens">
              <Zap size={16} /> Refresh FB Tokens
            </button>
            <button onClick={() => setShowFbImport(true)} className="op-btn-secondary" data-testid="brand-fb-import">
              <Facebook size={16} /> Import from Facebook
            </button>
            <button onClick={openCreate} className="op-btn-primary" data-testid="brand-new">
              <Plus size={16} /> Add Brand
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="op-card p-10 text-center text-gray-500">Loading brands…</div>
      ) : brands.length === 0 ? (
        <div className="op-card">
          <EmptyState
            icon={Building2}
            title="No brands yet"
            description="Create your first brand to start running campaigns. Each brand gets its own settings, audience, and CRM pipeline."
            action={<button onClick={openCreate} className="op-btn-primary"><Plus size={16} /> Add Brand</button>}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((b) => (
            <div key={b.id} className="op-card p-5" data-testid={`brand-card-${b.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                         style={{ backgroundColor: b.primary_color || "#7C3AED" }}>
                      {b.name.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{b.name}</h3>
                    <p className="text-xs text-gray-500 truncate">/{b.slug}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(b)} title="Edit" className="p-1.5 text-gray-500 hover:text-[#7C3AED] hover:bg-gray-50 rounded" data-testid={`brand-edit-${b.slug}`}>
                    <Edit2 size={15} />
                  </button>
                  <button onClick={() => handleDelete(b)} title="Delete" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-50 rounded" data-testid={`brand-delete-${b.slug}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem]">
                {b.audience_description || <span className="text-gray-400 italic">No audience description</span>}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-500">Tone:</span> <span className="text-gray-900">{b.tone || "—"}</span></div>
                <div><span className="text-gray-500">Budget/day:</span> <span className="text-gray-900">{b.daily_ad_budget != null ? `$${b.daily_ad_budget}` : "—"}</span></div>
                <div className="col-span-2 truncate"><span className="text-gray-500">Sender:</span> <span className="text-gray-900">{b.default_sender_email || "—"}</span></div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                <Link to={`/brands/${b.id}/style`} className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1" data-testid={`brand-style-${b.slug}`}>
                  <Sparkles size={12} /> Style Library
                </Link>
                <span className="text-gray-300">·</span>
                <button
                  onClick={() => copyLandingUrl(b)}
                  className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1"
                  data-testid={`brand-copy-landing-${b.slug}`}
                >
                  {copiedId === b.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === b.id ? "Copied!" : "Copy landing page URL"}
                </button>
                {me?.id && (
                  <a
                    href={`/lead/${me.id}/${b.slug}`}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-auto text-xs text-gray-500 hover:text-[#7C3AED] flex items-center gap-1"
                  >
                    Preview <ExternalLink size={11} />
                  </a>
                )}
              </div>

              {/* Meta connection status + actions */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <MetaConnectionBlock brand={b} onChange={load} />
              </div>
            </div>
          ))}
        </div>
      )}

      {showFbRefresh && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { if (!fbRefreshing) { setShowFbRefresh(false); setFbRefreshResult(null); setFbRefreshToken(""); } }}>
          <div className="bg-white rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={20} className="text-amber-500" />
                <h2 className="text-xl font-bold text-gray-900">Refresh Facebook Page Tokens</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">Page Access Tokens expire when User Tokens expire. Paste a FRESH User Access Token from <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-[#7C3AED] underline">Graph API Explorer</a> to refresh all your brand tokens at once (long-lived, never-expiring).</p>
              {fbRefreshResult ? (
                <div className="space-y-2 text-sm">
                  <p className="text-emerald-700 font-semibold">✅ Refreshed {fbRefreshResult.refreshed} brand(s)</p>
                  {fbRefreshResult.not_found > 0 && <p className="text-gray-600">⚠ {fbRefreshResult.not_found} Pages from your FB account had no matching brand</p>}
                  {fbRefreshResult.errors?.length > 0 && (
                    <div className="text-xs text-red-700">
                      <p className="font-semibold">Errors:</p>
                      <ul>{fbRefreshResult.errors.map((e, i) => <li key={i}>• {e.page_name}: {e.detail}</li>)}</ul>
                    </div>
                  )}
                  <button onClick={() => { setShowFbRefresh(false); setFbRefreshResult(null); setFbRefreshToken(""); }} className="op-btn-primary w-full mt-3">Done</button>
                </div>
              ) : (
                <form onSubmit={runFbRefresh} className="space-y-3">
                  <textarea rows={4} className="op-input font-mono text-xs" value={fbRefreshToken} onChange={(e) => setFbRefreshToken(e.target.value)} placeholder="EAAB..." data-testid="fb-refresh-token" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowFbRefresh(false)} className="op-btn-secondary flex-1" disabled={fbRefreshing}>Cancel</button>
                    <button type="submit" disabled={fbRefreshing} className="op-btn-primary flex-1" data-testid="fb-refresh-submit">{fbRefreshing ? "Refreshing…" : "Refresh All Tokens"}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {showFbImport && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { if (!fbImporting) { setShowFbImport(false); setFbResult(null); setFbToken(""); } }}>
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Facebook size={20} className="text-[#1877F2]" />
                <h2 className="text-xl font-bold text-gray-900">Bulk Import from Facebook</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">Paste your User Access Token from <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-[#7C3AED] underline">Graph API Explorer</a>. We'll create one Brand per Facebook Page you manage, with the page ID + access token already wired up.</p>

              {fbResult ? (
                <div className="space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
                    <p className="text-sm font-semibold text-emerald-900">✅ Import complete</p>
                    <div className="text-sm text-emerald-800 mt-1">
                      <strong>{fbResult.imported}</strong> new brands created · <strong>{fbResult.subscribed}</strong> auto-subscribed to lead webhook · <strong>{fbResult.skipped}</strong> already linked
                    </div>
                  </div>
                  {fbResult.details?.length > 0 && (
                    <div className="text-xs">
                      <p className="font-semibold text-gray-700 mb-1">Imported:</p>
                      <ul className="space-y-0.5 text-gray-600 max-h-32 overflow-y-auto">
                        {fbResult.details.map((d) => <li key={d.brand_id}>• {d.page_name}</li>)}
                      </ul>
                    </div>
                  )}
                  {fbResult.skipped_details?.length > 0 && (
                    <div className="text-xs">
                      <p className="font-semibold text-gray-500 mb-1">Skipped:</p>
                      <ul className="space-y-0.5 text-gray-500 max-h-24 overflow-y-auto">
                        {fbResult.skipped_details.map((d) => <li key={d.page_id}>• {d.page_name} — {d.reason}</li>)}
                      </ul>
                    </div>
                  )}
                  {fbResult.errors?.length > 0 && (
                    <div className="text-xs text-red-700">
                      <p className="font-semibold mb-1">Subscribe errors:</p>
                      <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                        {fbResult.errors.map((e, i) => <li key={i}>• {e.page_name}: {e.detail}</li>)}
                      </ul>
                    </div>
                  )}
                  <button onClick={() => { setShowFbImport(false); setFbResult(null); setFbToken(""); }} className="op-btn-primary w-full">Done</button>
                </div>
              ) : (
                <form onSubmit={runFbImport} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User Access Token *</label>
                    <textarea rows={4} className="op-input font-mono text-xs" value={fbToken} onChange={(e) => setFbToken(e.target.value)} placeholder="EAAB... (paste the token from Graph API Explorer)" data-testid="fb-bulk-token" />
                    <p className="text-[11px] text-gray-400 mt-1">Must have these permissions: pages_show_list, pages_read_engagement, pages_manage_ads, leads_retrieval</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={fbAutoSubscribe} onChange={(e) => setFbAutoSubscribe(e.target.checked)} />
                    Also auto-subscribe each page to lead webhook
                  </label>
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={() => setShowFbImport(false)} className="op-btn-secondary flex-1" disabled={fbImporting}>Cancel</button>
                    <button type="submit" disabled={fbImporting} className="op-btn-primary flex-1" data-testid="fb-bulk-submit">{fbImporting ? "Importing…" : "Import All Pages"}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={save} className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{editing ? "Edit Brand" : "New Brand"}</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input className="op-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Study Alliance" data-testid="brand-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                  <div className="flex items-center gap-3">
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="logo" className="w-16 h-16 rounded-lg object-contain border border-gray-200 bg-gray-50" data-testid="brand-logo-preview" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">No logo</div>
                    )}
                    <div className="flex-1 space-y-1.5">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (!editing?.id) {
                            // Brand isn't saved yet — embed as data URL locally; backend upload happens after first save
                            const reader = new FileReader();
                            reader.onload = () => setForm({ ...form, logo_url: reader.result });
                            reader.readAsDataURL(f);
                            return;
                          }
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            const { data } = await api.post(`/brands/${editing.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                            setForm({ ...form, logo_url: data.logo_url });
                            toast.success("Logo updated");
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Upload failed");
                          }
                          e.target.value = "";
                        }}
                        className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-50 file:text-[#7C3AED] file:font-semibold hover:file:bg-purple-100"
                        data-testid="brand-logo-input"
                      />
                      {form.logo_url && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, logo_url: "" })}
                          className="text-xs text-rose-600 hover:underline"
                          data-testid="brand-logo-remove"
                        >
                          Remove logo
                        </button>
                      )}
                      <p className="text-[10px] text-gray-400">PNG, JPEG, SVG or WebP · max 1.5 MB. Logo appears on AI-generated flyers.</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                    <input className="op-input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} placeholder="Educational, supportive" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <input className="op-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Education" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target audience</label>
                  <textarea rows={2} className="op-input" value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })} placeholder="Students 18-25 looking for IELTS prep" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default sender email</label>
                    <input type="email" className="op-input" value={form.default_sender_email} onChange={(e) => setForm({ ...form, default_sender_email: e.target.value })} placeholder="hello@brand.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default sender phone</label>
                    <input className="op-input" value={form.default_sender_phone} onChange={(e) => setForm({ ...form, default_sender_phone: e.target.value })} placeholder="+1..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input className="op-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Daily ad budget ($)</label>
                    <input type="number" min="0" className="op-input" value={form.daily_ad_budget} onChange={(e) => setForm({ ...form, daily_ad_budget: e.target.value })} placeholder="25" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand color</label>
                  <input type="color" className="h-10 w-20" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => setShowModal(false)} className="op-btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="op-btn-primary flex-1" data-testid="brand-save">
                  {saving ? "Saving…" : editing ? "Save" : "Create Brand"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

// ============ Meta Connection sub-component ============
const MetaConnectionBlock = ({ brand, onChange }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ fb_page_id: "", fb_page_access_token: "", fb_page_name: "" });
  const [busy, setBusy] = useState(false);

  const isConnected = !!brand.fb_page_id;
  const isSubscribed = !!brand.meta_subscribed_at;

  const connect = async (e) => {
    e.preventDefault();
    if (!form.fb_page_id.trim() || !form.fb_page_access_token.trim()) {
      toast.error("Page ID and Access Token are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/brands/${brand.id}/meta/connect`, form);
      toast.success("Facebook Page connected");
      setShow(false);
      setForm({ fb_page_id: "", fb_page_access_token: "", fb_page_name: "" });
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connect failed");
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post(`/brands/${brand.id}/meta/subscribe`);
      toast.success("Page subscribed to Lead webhook — new ad leads will auto-import");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscribe failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect this Facebook Page from the brand? Existing leads stay.")) return;
    setBusy(true);
    try {
      await api.post(`/brands/${brand.id}/meta/disconnect`);
      toast.success("Disconnected");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isConnected) {
    return (
      <>
        <button
          onClick={() => setShow(true)}
          className="text-xs flex items-center gap-1.5 text-gray-600 hover:text-[#7C3AED]"
          data-testid={`brand-meta-connect-${brand.slug}`}
        >
          <Link2 size={12} /> Connect Facebook Page for Lead Ads
        </button>
        {show && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShow(false)}>
            <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <form onSubmit={connect} className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Connect Facebook Page</h2>
                <p className="text-xs text-gray-500 mb-4">Brand: <strong>{brand.name}</strong></p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Facebook Page ID *</label>
                    <input className="op-input" value={form.fb_page_id} onChange={(e) => setForm({ ...form, fb_page_id: e.target.value })} placeholder="e.g., 1234567890123456" data-testid="meta-page-id" />
                    <p className="text-[11px] text-gray-400 mt-1">Found in Facebook Page → About → Page Transparency</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Page Name (optional)</label>
                    <input className="op-input" value={form.fb_page_name} onChange={(e) => setForm({ ...form, fb_page_name: e.target.value })} placeholder="Study Alliance" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Long-Lived Page Access Token *</label>
                    <textarea rows={3} className="op-input font-mono text-xs" value={form.fb_page_access_token} onChange={(e) => setForm({ ...form, fb_page_access_token: e.target.value })} placeholder="EAAB...long-token...XYZ" data-testid="meta-page-token" />
                    <p className="text-[11px] text-gray-400 mt-1">Get from Graph API Explorer with permissions: pages_show_list, pages_manage_metadata, leads_retrieval, pages_read_engagement</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setShow(false)} className="op-btn-secondary flex-1">Cancel</button>
                  <button type="submit" disabled={busy} className="op-btn-primary flex-1" data-testid="meta-connect-submit">{busy ? "Connecting…" : "Connect"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-emerald-600">
          <Check size={12} /> Page connected
        </span>
        <span className="text-gray-500">·</span>
        <span className="text-gray-600">{brand.fb_page_name || brand.fb_page_id}</span>
        <button onClick={disconnect} className="ml-auto text-gray-400 hover:text-red-600" title="Disconnect" data-testid={`brand-meta-disconnect-${brand.slug}`}>
          <ZapOff size={12} />
        </button>
      </div>
      {isSubscribed ? (
        <div className="flex items-center gap-1 text-[#7C3AED]">
          <Zap size={12} /> Lead webhook active — auto-importing new ad leads
        </div>
      ) : (
        <button onClick={subscribe} disabled={busy} className="w-full py-1.5 text-xs bg-[#7C3AED] text-white rounded font-medium hover:bg-[#6D28D9] disabled:opacity-50" data-testid={`brand-meta-subscribe-${brand.slug}`}>
          {busy ? "Subscribing…" : "▸ Activate Lead Webhook"}
        </button>
      )}
    </div>
  );
};

export default Brands;
